import { getReadyAuthUser } from "~/utils/auth/waitForAuthReady";
import {
  ActionFunctionArgs,
  LoaderFunctionArgs,
  redirect,
} from "react-router-dom";
import { apiClient, LeaseTableLoaderResult } from "~/services/apiClient";
import {
  EntityListData,
  FormattedLeaseData,
  NewLeaseFormPayload,
} from "~/types";
import { getCachedAuthAccessInfo, INTENTS, loadCookiesData } from "~/utils";
import {
  createServerFilteredShouldRevalidate,
  normalizeSearchParams,
  setLoaderQueryData,
  STALE_TIME_TIER_C,
  withEntityCacheInvalidation,
} from "~/utils/loaderQueryCache";
import {
  leaseFiltersToLoaderParams,
  parseLeaseListFilters,
} from "../utils/parseLeaseListFilters";
import * as APITypes from "~/API";

export type LeaseTablePageLoaderType = {
  tenantList: APITypes.Tenant[];
  leaseList: FormattedLeaseData[];
  unitListMap: EntityListData;
};

export function leaseTableQueryKey(
  ownerID: string,
  ownerType: APITypes.OwnerType,
  search: string,
) {
  return [
    "lease-table",
    ownerID,
    ownerType,
    normalizeSearchParams(search),
  ] as const;
}

export const leaseTableShouldRevalidate = createServerFilteredShouldRevalidate(
  (owner, url) =>
    leaseTableQueryKey(owner.ownerID, owner.ownerType, url.search),
  STALE_TIME_TIER_C,
);

function formatLeaseTableResult(
  result: LeaseTableLoaderResult,
): LeaseTablePageLoaderType {
  const rawLeaseList = result.leaseList ?? [];
  const formattedLeaseList: FormattedLeaseData[] = rawLeaseList.map(
    (item) =>
      ({
        leaseInfo: item.leaseInfo,
        tenantInfo: item.tenantInfo,
        entityType: item.entityType,
        entityDetails: item.entityDetails,
        advanceLeaseAction: "_Review_Application",
        cancelLeaseAction: "_Deny_Application",
      }) as FormattedLeaseData,
  );

  const unitListFromLambda = result.unitList ?? [];
  const propertyList = result.propertyList ?? [];
  const unitList = unitListFromLambda.map((u) => u.unitDetails);
  const propertyMap: Record<string, APITypes.Property> = {};
  propertyList.forEach((p) => {
    propertyMap[p.id] = p;
  });
  const buildingMap: Record<string, APITypes.Building> = {};
  unitListFromLambda.forEach((u) => {
    if (u.buildingDetails?.id)
      buildingMap[u.buildingDetails.id] = u.buildingDetails;
  });
  const unitMap: Record<string, APITypes.Unit> = {};
  unitList.forEach((u) => {
    unitMap[u.id] = u;
  });

  const unitListMap: EntityListData = {
    propertyList: [],
    buildingList: [],
    unitList,
    serviceRequestList: [],
    amenityList: [],
    propertyMap,
    buildingMap,
    unitMap,
  };

  return {
    leaseList: formattedLeaseList,
    tenantList: result.tenantList ?? [],
    unitListMap,
  };
}

export async function fetchLeaseTableData(
  request: Request,
): Promise<LeaseTablePageLoaderType | Response> {
  const user = await getReadyAuthUser();
  if (!user) return redirect("/explore");

  const authAccessInfo = await loadCookiesData(user.uid);
  if (!authAccessInfo.success) return redirect("/explore");

  const { ownerID, ownerType } = authAccessInfo.data;
  const filters = parseLeaseListFilters(new URL(request.url).searchParams);

  const result = await apiClient.loader<LeaseTableLoaderResult>(
    "lease-table",
    leaseFiltersToLoaderParams(filters, { ownerID, ownerType }),
  );

  if (!result.success || !result.data) {
    console.error("Failed to load leases:", result.error);
    return redirect("/explore");
  }

  return formatLeaseTableResult(result.data);
}

export const LeaseTablePageLoader = async ({
  request,
}: LoaderFunctionArgs): Promise<LeaseTablePageLoaderType | Response> => {
  const data = await fetchLeaseTableData(request);
  if (data instanceof Response) {
    return data;
  }

  const auth = getCachedAuthAccessInfo();
  if (auth) {
    const key = leaseTableQueryKey(
      auth.ownerID,
      auth.ownerType,
      new URL(request.url).search,
    );
    setLoaderQueryData(key, data, STALE_TIME_TIER_C);
  }

  return data;
};

export const LeaseTablePageAction = async ({ request }: ActionFunctionArgs) => {
  const formData = await request.formData();
  const intent = formData.get("intent");
  const user = await getReadyAuthUser();
  if (!user) return redirect("/explore");

  if (intent !== INTENTS._add_lease) {
    return null;
  }

  const authAccessInfo = await loadCookiesData(user.uid);
  if (!authAccessInfo.success) return redirect("/explore");
  const { ownerID, ownerType } = authAccessInfo.data;

  return withEntityCacheInvalidation(
    [{ entity: "lease" }, { entity: "unit" }],
    async () => {
      const payload = JSON.parse(
        formData.get("body") as string,
      ) as NewLeaseFormPayload;
      // ownerID/ownerType come from cookies (org ID when in an org — never Firebase uid).
      // apiClient also attaches ownerContext; keep these aligned explicitly.
      return apiClient.sendIntent<APITypes.Lease>(INTENTS._add_lease, {
        entityID: payload.entityID,
        tenantID: payload.tenantID,
        type: payload.type,
        startDate: payload.startDate,
        endDate: payload.endDate,
        markedDate: payload.markedDate,
        extraOccupants: payload.extraOccupants,
        normalizeRent: Boolean(payload.normalizeRent),
        ownerID,
        ownerType,
      });
    },
  );
};
